import './styles/index.css'
import './styles/board.css'
import './styles/modal.css'
import './styles/hintToast.css'
import './styles/warmup.css'
import './styles/gameOverBanner.css'

import { LOADED_BUILD_ID } from './game/buildInfo'
import { createGameController } from './game/controller'
import {
  canReload,
  canRollOver,
  createRefreshCoordinator,
} from './game/refreshCoordinator'
import type { DisruptInputs } from './game/refreshCoordinator'
import { corpusEntryToPuzzle } from './game/corpus'
import {
  COUNTDOWN_READY_LABEL,
  formatLocalDate,
  getCountdownLabel,
  getDailyPuzzleEntry,
  getDayOffset,
  getPuzzleNumber,
  endOfPuzzleDay,
} from './game/daily'
import {
  SCHEMA_VERSION,
  getOnboardedAt,
  loadPersistedState,
  resetTodayPreservingStats,
  savePersistedState,
  serializeCompleted,
  setOnboardedAt,
  wipeAllPersistence,
} from './game/persistence'
import {
  getPracticePuzzle,
  isPracticeAvailable,
} from './game/practice'
import {
  type PracticeCompletionRecord,
  PRACTICE_PUZZLE_COUNT,
  recordPracticeCompletion,
} from './game/practiceProgress'
import type { Puzzle } from './game/puzzle'
import { renderGameOverBanner, renderPuzzleSubtitle } from './game/render'
import { loadHintFlags, saveHintFlags } from './game/hintFlags'
import {
  type MountScreenHooks,
  clearWarmUpCompletedAt,
  createWarmUpSequencer,
  getWarmUpCompletedAt,
  setWarmUpCompletedAt,
} from './game/warmup'
import {
  isWarmUpDialogOpen,
  showPracticeComplete,
  showWarmUpComplete,
  showWarmUpWelcome,
} from './game/warmupWelcome'
import { generateShareGrid, SHARE_URL } from './game/sharegrid'
import { computeSlotStates, type SlotState } from './game/slotStates'
import type { GameState } from './game/state'
import { playLossAnimation, restoreBoardAfterLoss } from './game/lossAnimation'
import { playVictoryAnimation } from './game/victoryAnimation'
import {
  type AppSettings,
  DEFAULT_SETTINGS,
  applySettings,
} from './game/settings'
import {
  type LifetimeStats,
  computeGrossFlickCount,
  emptyLifetimeStats,
  updateStatsOnGameEnd,
} from './game/stats'
import {
  type EndgameContent,
  type EndgameElements,
  type EndgameNextAction,
  closeEndgame,
  closeOnboarding,
  showEndgame,
  showOnboarding,
} from './ui/modal'
import { showHintToast } from './ui/hintToast'
import { resetDoubleTapState } from './ui/noDoubleTapZoom'
import { installResumeReconciler } from './ui/resumeReconciler'
import { triggerShare } from './ui/share'
import {
  type StatsSectionElements,
  renderStatsSection,
} from './ui/statsRender'

// Orchestrator entry point.
//
// Wires the DOM landmarks to the GameController, manages onboarding gating,
// owns the LifetimeStats snapshot, and brokers terminal-state events between
// the game and the endgame modal.
//
// Lifecycle:
//   1. Look up all DOM landmarks. Fail loud if any are missing — that means
//      index.html is out of sync with this file's expectations.
//   2. Load persisted state (incl. lifetime stats) up front so the stats
//      snapshot is available before the controller mounts.
//   3. If the player has not completed onboarding, show the onboarding modal
//      and DEFER mounting the game until the player clicks "Got it." This
//      avoids the multi-modal race entirely (no board → no flicks → no
//      premature endgame transitions). Subsequent visits skip onboarding and
//      mount immediately.
//   4. mountDaily() figures out whether to start fresh, restore in-progress
//      state, or restore a completed game. In the completed branch, the
//      controller's `onGameEnd` callback fires once after the initial render
//      (microtask-deferred) with source='restored', so we open the modal
//      without re-running the lifetime-stats update.

interface HintPolicy {
  readonly canFireBlue: () => boolean
  readonly markBlueFired: () => void
  readonly canFireOrange: () => boolean
  readonly markOrangeFired: () => void
  readonly canFireIncorrect: () => boolean
  readonly markIncorrectFired: () => void
}

// Builds the onBarChange callback that the controller fires after every
// successful flick's render. Holds a per-mount snapshot of the previous slot
// states so transitions can be detected ("slot just turned blue"). Caller
// supplies the mode-specific policy — daily uses persisted flags, practice
// uses an in-memory per-mount flag for blue + always-fire for orange.
function createHintOnBarChange(
  policy: HintPolicy,
): (state: GameState, p: Puzzle) => void {
  let lastSlotStates: readonly SlotState[] | null = null
  return (state, p): void => {
    const curr = computeSlotStates(state, p)
    let newBlueIdx: number | null = null
    let newOrangeIdx: number | null = null
    let newIncorrectIdx: number | null = null
    for (let i = 0; i < curr.length; i += 1) {
      const prev: SlotState = lastSlotStates?.[i] ?? 'empty'
      const c = curr[i]
      if (c === 'correct-canonical' && prev !== 'correct-canonical' && newBlueIdx === null) {
        newBlueIdx = i
      }
      if (c === 'correct-alt' && prev !== 'correct-alt' && newOrangeIdx === null) {
        newOrangeIdx = i
      }
      if (c === 'incorrect' && prev !== 'incorrect' && newIncorrectIdx === null) {
        newIncorrectIdx = i
      }
    }
    lastSlotStates = curr
    // Priority: blue > orange > incorrect. Each branch self-gates on its own
    // policy flag so a player who's already seen the higher-priority hint
    // falls through to the next when this flick triggers more than one.
    if (newBlueIdx !== null && policy.canFireBlue()) {
      showHintToast('Blue shows the right letter, right flick')
      policy.markBlueFired()
    } else if (newOrangeIdx !== null && policy.canFireOrange()) {
      showHintToast('Orange shows the right letter, wrong flick. Tap Undo!', { variant: 'orange' })
      policy.markOrangeFired()
    } else if (newIncorrectIdx !== null && policy.canFireIncorrect()) {
      showHintToast('Grey shows incorrect letter placement. Tap Undo!', { variant: 'black' })
      policy.markIncorrectFired()
    }
  }
}

// Tap-to-explain: when the player taps a word-bar slot, fire the same hint
// toast that the auto-policy would (but unconditionally — a tap is a
// deliberate query, never policy-gated). Empty slots produce no toast.
function showHintForSlot(
  state: GameState,
  p: Puzzle,
  slotIndex: number,
): void {
  const slots = computeSlotStates(state, p)
  const slot = slots[slotIndex]
  if (slot === 'correct-canonical') {
    showHintToast('Blue shows the right letter, right flick')
  } else if (slot === 'correct-alt') {
    showHintToast('Orange shows the right letter, wrong flick. Tap Undo!', { variant: 'orange' })
  } else if (slot === 'incorrect') {
    showHintToast('Grey shows incorrect letter placement. Tap Undo!', { variant: 'black' })
  }
}

function main(): void {
  // ---------- DOM landmark lookup ----------
  const boardRoot = document.getElementById('board-root')
  const barRoot = document.getElementById('bar-root')
  const puzzleSubtitleRoot = document.getElementById('puzzle-subtitle')
  const undoButton = document.getElementById('undo-button')
  // Game-over banner: the real <button> that overlays the board's bottom rows
  // at game end, both announcing the terminal state (via its aria-live host)
  // and reopening the endgame modal. Replaces the old status line + the
  // separate "View results" button.
  const gameOverBanner = document.getElementById('gameover-banner')
  const settingsButton = document.getElementById('settings-button')
  const helpButton = document.getElementById('help-button')
  const helpLabelEl = document.getElementById('help-label')
  const brandTitleEl = document.getElementById('brand-title')
  const settingsDialogEl = document.getElementById('settings-modal')
  const settingsCloseBtnEl = document.getElementById('settings-close')
  const settingsResetBtnEl = document.getElementById('settings-reset')
  const settingsPracticeBtnEl = document.getElementById('settings-practice')

  const aboutButton = document.getElementById('about-button')
  const aboutDialogEl = document.getElementById('about-modal')
  const aboutCloseBtnEl = document.getElementById('about-close')
  const aboutDoneBtnEl = document.getElementById('about-done')

  const onboardingDialogEl = document.getElementById('onboarding-modal')
  const onboardingCloseBtnEl = document.getElementById('onboarding-close')
  const endgameDialogEl = document.getElementById('endgame-modal')
  const endgameCloseBtnEl = document.getElementById('endgame-close')
  const endgameTitleEl = document.getElementById('endgame-title')
  const endgameLoseRevealEl = document.getElementById('endgame-lose-reveal')
  const endgameSubheadEl = document.getElementById('endgame-subhead')
  const endgameGridEl = document.getElementById('endgame-grid')
  const endgameTallyEl = document.getElementById('endgame-tally')
  const endgameCountdownEl = document.getElementById('endgame-countdown')
  const endgameShareBtnEl = document.getElementById('endgame-share')
  const endgameNextActionsEl = document.getElementById('endgame-next-actions')
  // Manual-copy fallback for insecure contexts (http:// over a LAN IP) where
  // navigator.share + clipboard are both gated. May be null if old HTML cache.
  const endgameShareTextEl = document.getElementById('endgame-share-text')

  // Practice mode landmarks. The nav element is hidden by default in
  // index.html; renderPracticeNav unhides + populates it on entry to a
  // practice puzzle, hidePracticeNav re-hides on entry to daily / dev.
  const practiceNavEl = document.getElementById('practice-nav')
  const practicePrevBtnEl = document.getElementById('practice-prev')
  const practicePositionEl = document.getElementById('practice-position')
  const practiceNextBtnEl = document.getElementById('practice-next')
  // The Warm Up button sits inside the practice-nav row but is shown only
  // when the active practice puzzle is #1 (PIE — the puzzle the Warm Up
  // replay is scripted for).
  const warmUpReplayButtonEl = document.getElementById('warmup-replay-button')
  // Warm-up gate caption host inside .board-stage. Empty/inert except while
  // the Warm Up gate is mounted.
  const warmUpCaptionRootEl = document.getElementById('warmup-caption-root')

  // Phase 6 stats DOM landmarks.
  const statsSummaryRowEl = document.getElementById('stats-summary-row')
  const statsStreakRowEl = document.getElementById('stats-streak-row')
  const statsSundayRowEl = document.getElementById('stats-sunday-row')

  if (
    boardRoot === null ||
    barRoot === null ||
    puzzleSubtitleRoot === null ||
    endgameTitleEl === null ||
    endgameLoseRevealEl === null ||
    endgameSubheadEl === null ||
    endgameGridEl === null ||
    endgameTallyEl === null ||
    endgameCountdownEl === null ||
    endgameNextActionsEl === null ||
    statsSummaryRowEl === null ||
    statsStreakRowEl === null ||
    statsSundayRowEl === null ||
    practiceNavEl === null ||
    practicePositionEl === null ||
    warmUpCaptionRootEl === null
  ) {
    throw new Error(
      'Required DOM landmarks missing — check index.html against main.ts',
    )
  }
  if (!(warmUpReplayButtonEl instanceof HTMLButtonElement)) {
    throw new Error('#warmup-replay-button must be a <button>')
  }
  if (!(practicePrevBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#practice-prev must be a <button>')
  }
  if (!(practiceNextBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#practice-next must be a <button>')
  }
  if (!(undoButton instanceof HTMLButtonElement)) {
    throw new Error('#undo-button must be a <button>')
  }
  if (!(gameOverBanner instanceof HTMLButtonElement)) {
    throw new Error('#gameover-banner must be a <button>')
  }
  if (!(onboardingDialogEl instanceof HTMLDialogElement)) {
    throw new Error('#onboarding-modal must be a <dialog>')
  }
  if (!(helpButton instanceof HTMLButtonElement)) {
    throw new Error('#help-button must be a <button>')
  }
  if (!(endgameDialogEl instanceof HTMLDialogElement)) {
    throw new Error('#endgame-modal must be a <dialog>')
  }
  if (!(endgameCloseBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#endgame-close must be a <button>')
  }
  if (!(endgameShareBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#endgame-share must be a <button>')
  }
  if (!(settingsButton instanceof HTMLButtonElement)) {
    throw new Error('#settings-button must be a <button>')
  }
  if (!(settingsDialogEl instanceof HTMLDialogElement)) {
    throw new Error('#settings-modal must be a <dialog>')
  }
  if (!(settingsCloseBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#settings-close must be a <button>')
  }
  if (!(onboardingCloseBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#onboarding-close must be a <button>')
  }
  if (!(settingsResetBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#settings-reset must be a <button>')
  }
  if (!(brandTitleEl instanceof HTMLHeadingElement)) {
    throw new Error('#brand-title must be a heading')
  }
  if (!(settingsPracticeBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#settings-practice must be a <button>')
  }
  if (!(aboutButton instanceof HTMLButtonElement)) {
    throw new Error('#about-button must be a <button>')
  }
  if (!(aboutDialogEl instanceof HTMLDialogElement)) {
    throw new Error('#about-modal must be a <dialog>')
  }
  if (!(aboutCloseBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#about-close must be a <button>')
  }
  if (!(aboutDoneBtnEl instanceof HTMLButtonElement)) {
    throw new Error('#about-done must be a <button>')
  }

  // Re-bind to typed consts so closures below see the narrowed types.
  // Avoids `!` non-null assertions.
  const boardEl: HTMLElement = boardRoot
  const barEl: HTMLElement = barRoot
  const puzzleSubtitleEl: HTMLElement = puzzleSubtitleRoot
  const undoEl: HTMLButtonElement = undoButton
  const bannerEl: HTMLButtonElement = gameOverBanner
  const practiceNavRoot: HTMLElement = practiceNavEl
  const practicePositionRoot: HTMLElement = practicePositionEl
  const practicePrevBtn: HTMLButtonElement = practicePrevBtnEl
  const practiceNextBtn: HTMLButtonElement = practiceNextBtnEl
  const warmUpReplayBtn: HTMLButtonElement = warmUpReplayButtonEl
  const warmUpCaptionRoot: HTMLElement = warmUpCaptionRootEl
  // Re-bind narrowed types for closures used by syncPracticeCtaVisibility (a
  // function declaration; TS doesn't carry an inline instanceof narrowing
  // across hoisted function bodies). The auto-refresh safe predicate
  // (isAnyDisruptiveModalOpen) is another such closure, so all four full-screen
  // dialogs get typed re-binds here.
  const onboardingDialog: HTMLDialogElement = onboardingDialogEl
  const endgameDialog: HTMLDialogElement = endgameDialogEl
  const settingsDialog: HTMLDialogElement = settingsDialogEl
  const aboutDialog: HTMLDialogElement = aboutDialogEl

  const endgameElements: EndgameElements = {
    dialog: endgameDialogEl,
    title: endgameTitleEl,
    loseReveal: endgameLoseRevealEl,
    subhead: endgameSubheadEl,
    grid: endgameGridEl,
    tally: endgameTallyEl,
    countdown: endgameCountdownEl,
    closeButton: endgameCloseBtnEl,
    shareButton: endgameShareBtnEl,
    nextActionsContainer: endgameNextActionsEl,
  }

  const statsEls: StatsSectionElements = {
    summaryRow: statsSummaryRowEl,
    streakRow: statsStreakRowEl,
    sundayRow: statsSundayRowEl,
  }

  const controller = createGameController()

  // ---------- Lifetime stats snapshot ----------
  // Loaded once at startup from the persisted v2 payload (the loader handles
  // v1→v2 migration transparently). Mutated by handleGameEnd on every live
  // terminal transition. The controller picks up the latest snapshot via the
  // `getStats` callback at persist time so the on-disk payload always reflects
  // the same stats values that the modal is about to display.
  const persistedAtStart = loadPersistedState()
  let currentStats: LifetimeStats =
    persistedAtStart !== null ? persistedAtStart.stats : emptyLifetimeStats()
  let currentSettings: AppSettings =
    persistedAtStart !== null ? persistedAtStart.settings : DEFAULT_SETTINGS
  // Apply theme before first paint so the board doesn't flash the wrong colors.
  applySettings(currentSettings)

  // The most recent endgame content. Held so the share button + the view-
  // results button can re-use the same generated share text without
  // recomputing — and so reopening the modal shows identical content.
  let latestEndgameContent: EndgameContent | null = null
  let latestShareText: string | null = null
  // Reverts the Share button label from "Copied!" back to "Share" after the
  // copy-path flip (see handleEndgameShare). Held so re-clicks restart it and so
  // reopening the modal can cancel a still-pending revert.
  let shareCopiedRevertTimer: ReturnType<typeof setTimeout> | null = null

  // Ticks the endgame "Next Letter Flick in …" countdown once per second while
  // the results modal is open, counting down to the current puzzle-day's fixed
  // rollover instant (endOfPuzzleDay(currentPuzzleDate)). Cleared on close and
  // before each reopen so no interval leaks, and self-clears once the target is
  // reached (the label is then "ready" and won't change). Daily/dev only —
  // practice has no countdown.
  let countdownTimer: ReturnType<typeof setInterval> | null = null

  // The user-facing puzzle number of the currently mounted puzzle. Set by
  // each mount path so the subtitle, share grid, and endgame modal all agree
  // on the same number. Initialised to today's daily number as a safe default
  // — every mount path overwrites it before any endgame content is built.
  let currentPuzzleNumber: number = getPuzzleNumber()

  // The currently-mounted daily puzzle's own date ('YYYY-MM-DD'), captured from
  // the corpus entry at mount time so the endgame modal shows the PUZZLE's date,
  // not today's clock date (the two desync when playing across midnight). Daily
  // and dev mounts set it; practice has no date concept and ignores it.
  let currentPuzzleDate: string = getDailyPuzzleEntry().letter_flick_puzzle_date

  // The 1-indexed practice puzzle currently mounted, or null when the active
  // mount is daily. Used by the prev/next nav handlers and by handleGameEnd's
  // practice branch (recording completion + advancing the unlock cursor).
  // Reset to null on every non-practice mount.
  let currentPracticePuzzleNumber: number | null = null

  // Which mount path is currently active. Captured by each mount path's
  // onGameEnd closure so handleGameEnd can branch on persistence behaviour
  // (daily persists, practice writes to the practice progress blob) and the
  // endgame modal can show the right header / next-action buttons.
  type GameMode = 'daily' | 'practice'

  // Live mode tracked explicitly (not inferred) so the refresh coordinator's
  // safe predicate can gate Tier 1 / Tier 2 on `currentMode === 'daily'`. EVERY
  // mount entry point sets this (design §13 #6); a missed assignment would make
  // the predicate lie. Initialised to 'daily' as the most common landing mode;
  // every mount path overwrites it before the coordinator consults it.
  let currentMode: GameMode = 'daily'

  // ---------- End-of-game race guard ----------
  // `handleGameEnd` is async: on a win it awaits the (2.4-3.6s) victory
  // animation BEFORE rendering the banner + opening the results modal. If the
  // board is remounted during that await (tap Next/Prev, help→Play, the brand
  // title, a daily rollover), the stale tail must NOT paint the finished
  // puzzle's modal + banner over the NEW puzzle.
  //
  // `mountGeneration` is bumped by every mount (via bumpMountGeneration()).
  // handleGameEnd captures it at entry and drops its UI tail if it changed
  // during the await (the win is still recorded synchronously before the await;
  // only the now-stale celebration is suppressed). Covers ALL remount paths.
  //
  // `activeEndAnimation` is the in-flight win OR loss animation, if any.
  // bumpMountGeneration() cancels it, so navigating (Next/Prev) immediately
  // finishes the animation — instant puzzle swap, no spin left painting on the
  // next puzzle's tiles, and (for a loss) no stranded overflow lock.
  let mountGeneration = 0
  let activeEndAnimation: { cancel(): void } | null = null

  function bumpMountGeneration(): void {
    mountGeneration += 1
    activeEndAnimation?.cancel()
    activeEndAnimation = null
  }

  // ---------- Warm Up gate state ----------
  // The single rule: until the Warm Up is completed (or skipped) once, the app
  // does NOTHING but the Warm Up — no daily, no practice, no welcome popup.
  //
  // `warmUpCompletedThisSession` is the private-mode hardening: a browser whose
  // localStorage writes silently fail would never see getWarmUpCompletedAt()
  // flip to non-null, which would leave every opener (help/brand/play) inert
  // forever after a completed warm-up. This in-memory mirror is set in BOTH
  // stamp sites (onComplete + skipWarmUp) so the openers come back to life for
  // the rest of the session even when the disk write didn't take.
  let warmUpCompletedThisSession = false

  // `warmUpActive` is the explicit re-entrancy guard for mountWarmUp. The
  // warm-up rebuilds the board unconditionally, so a second mountWarmUp while
  // one is live would tear down the in-flight curriculum. Set true at
  // mountWarmUp entry, cleared on EVERY exit (skip + both congrats forks). The
  // practice-1 "Warm Up" replay button calls mountWarmUp from practice mode,
  // where the flag is already false — that legit re-entry still works.
  let warmUpActive = false

  // Handle to the CURRENT warm-up mount's skip-affordance cleanup (the
  // removeSkipAffordance closure created inside mountWarmUp). The known exit
  // paths (skip + both congrats forks) call the closure directly — this
  // module-scope handle exists for SIDE-DOOR exits during a REPLAY (e.g. the
  // help modal's Let's Practice while the warm-up is live, when the openers
  // are unlocked), so every non-warm-up mount can strip the subtitle's
  // role/tabindex/aria-label/listeners no matter how the warm-up was left.
  // Cleanup is idempotent; null when no warm-up has installed the affordance.
  let clearWarmUpSkipAffordance: (() => void) | null = null

  // The one gate predicate. Reads the persisted flag first, then the in-memory
  // session mirror. Used by every player-facing door below.
  function isWarmUpDone(): boolean {
    return getWarmUpCompletedAt() !== null || warmUpCompletedThisSession
  }

  // Build the endgame content for the given terminal state. Pure function;
  // safe to call multiple times for the same state (e.g. reopen modal).
  // `mode` controls header text (`Letter Flick #N` vs `Letter Flick Practice
  // #N`), countdown visibility, and which (if any) next-action buttons render
  // alongside Share.
  function buildEndgameContent(
    s: GameState,
    p: Puzzle,
    mode: GameMode,
  ): EndgameContent {
    const won = s.status === 'won'
    const puzzleNumber = currentPuzzleNumber
    const isPractice = mode === 'practice'
    // Practice mode uses a different header in the share text so a shared
    // practice grid identifies as practice (not as the daily puzzle number,
    // which would mis-attribute the result to a different puzzle).
    const headerOverride = isPractice
      ? `Letter Flick Practice #${String(puzzleNumber)}`
      : undefined
    // Phase 9: per-slot classification for the final row, parallel-indexed
    // with finalBar. Reads the SAME gated source as the live bar
    // (computeSlotStates — DAG walk when live, per-slot union otherwise), so the
    // final row's blue/orange distribution matches exactly what the player saw
    // on screen at endgame. Sliced to the exited count so it stays parallel with
    // finalBar (computeSlotStates pads to wordLength with 'empty'; a loss can
    // exit fewer than wordLength letters).
    const computedSlotStates = computeSlotStates(s, p)
    const finalSlotStates = s.exitedLetters.map(
      (_letter, i) => computedSlotStates[i] ?? 'empty',
    )

    const share = generateShareGrid({
      log: s.eventLog,
      wordLength: s.wordLength,
      won,
      puzzleNumber,
      // Phase 8: share-grid now compares each bar slot to the target's letter
      // at that position to colour squares. targetWord is shipped lowercase
      // by the corpus loader; sharegrid normalises defensively but passing
      // it as-is keeps responsibilities clean.
      targetWord: p.targetWord,
      // The canonical end-state of the word bar; preferred over the event-log
      // simulation for the final row so the displayed state can't drift from
      // what the player actually sees on the board.
      finalBar: s.exitedLetters,
      finalSlotStates,
      // exactOptionalPropertyTypes: only set headerOverride when defined.
      ...(headerOverride !== undefined ? { headerOverride } : {}),
    })
    latestShareText = share.text

    // Win title splits onto two centered lines: "Solved!" on top, the
    // day's WORD below. The \n is rendered as a visual line-break by CSS
    // white-space: pre-line on #endgame-title (modal.css). Loss title
    // stays single line.
    const title = won
      ? `Solved!\n${p.targetWord.toUpperCase()}`
      : 'Out of undos.'
    const loseReveal = won ? '' : `The word was ${p.targetWord.toUpperCase()}.`
    // Daily subhead appends the long-form date so the modal shows what
    // puzzle / day the result corresponds to (matches the in-board
    // subtitle format). Practice and dev modes have no date concept.
    let subheadBase: string
    if (mode === 'practice') {
      subheadBase = `Letter Flick Practice #${String(puzzleNumber)}`
    } else {
      // The puzzle's OWN date (captured at mount), not today's clock date — so a
      // puzzle solved just after midnight still shows its own day, matching the
      // in-board subtitle and the puzzle number.
      const dateLabel = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(new Date(`${currentPuzzleDate}T00:00:00`))
      subheadBase = `Letter Flick #${String(puzzleNumber)} · ${dateLabel}`
    }
    const subhead = subheadBase
    const undoNoun = s.undoCount === 1 ? 'undo' : 'undos'
    // Phase 6: tally line reports GROSS flick count (including phantom flicks
    // that were later undone). state.flickCount is also gross — see state.ts —
    // but deriving from the event log keeps the share-grid stream as the
    // single source of truth for "what the player did this session".
    const grossFlicks = computeGrossFlickCount(s.eventLog)
    const flickNoun = grossFlicks === 1 ? 'flick' : 'flicks'
    const letterCount = p.targetWord.length
    const letterNoun = letterCount === 1 ? 'letter' : 'letters'
    const tally = `${String(letterCount)} ${letterNoun} · ${String(grossFlicks)} ${flickNoun} · ${String(s.undoCount)} ${undoNoun}`
    // Practice mode has no daily-rollover countdown — the puzzle is always
    // available. Empty string collapses via the .modal__countdown:empty rule.
    const countdown = isPractice
      ? ''
      : getCountdownLabel(endOfPuzzleDay(currentPuzzleDate))

    // Practice-only "next" affordances rendered alongside Share. Daily / dev
    // modes omit the nextActions field entirely so the modal's container stays
    // hidden. Each onClick is invoked AFTER the modal closes (modal.ts wraps
    // each listener: dialog.close() then action.onClick()), so we don't need
    // to close the modal explicitly inside the closures here.
    let nextActions: readonly EndgameNextAction[] | undefined
    if (isPractice) {
      const n = puzzleNumber
      // Next practice always wraps: from 10 back to 1. Shown on both win
      // and loss because practice is for learning, not testing — a stuck
      // player should be able to move on without first having to give up
      // via the help-button exit. Player exits practice mode entirely via
      // the ❓ help button → "Let's Play".
      const nextN = n >= PRACTICE_PUZZLE_COUNT ? 1 : n + 1
      nextActions = [
        { label: 'Replay', onClick: (): void => mountPractice(n) },
        { label: 'Next practice', onClick: (): void => mountPractice(nextN) },
      ]
    }

    return {
      title,
      loseReveal,
      subhead,
      gridRows: share.gridRows,
      gridAria: share.aria,
      tally,
      countdown,
      // exactOptionalPropertyTypes: omit the field entirely when undefined.
      ...(nextActions !== undefined ? { nextActions } : {}),
    }
  }

  // Open the endgame modal. Wires Share and Close handlers via showEndgame
  // (which detaches any prior listeners). Banner visibility is NOT touched
  // here: on the initial auto-open the banner is still hidden (it's revealed
  // by handleEndgameClose), and on a banner-tap reopen it stays visible
  // underneath — the <dialog> backdrop covers it and makes it inert. Hiding
  // it here caused a one-frame flash of the board behind it on tap. Renders
  // the stats section with the CURRENT snapshot — caller (handleGameEnd) is
  // responsible for having already updated currentStats on a 'live'
  // transition before invoking this.
  function openEndgameModal(content: EndgameContent): void {
    latestEndgameContent = content
    // Reset the manual-copy fallback element so a previous puzzle's failure
    // text doesn't linger if the user reopens the modal without re-tapping
    // Share.
    if (endgameShareTextEl !== null) {
      endgameShareTextEl.hidden = true
      endgameShareTextEl.textContent = ''
    }
    // Reset the Share button label in case a prior "Copied!" flip is still
    // pending when the modal is reopened (e.g. via the game-over banner).
    if (shareCopiedRevertTimer !== null) {
      clearTimeout(shareCopiedRevertTimer)
      shareCopiedRevertTimer = null
    }
    endgameElements.shareButton.textContent = 'Share'
    // Clear any prior tick (e.g. a banner-reopen while one was running) before
    // recomputing — no interval ever leaks.
    if (countdownTimer !== null) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
    // Recompute the live countdown on EVERY open so a banner-reopened modal
    // never shows a stale value baked at game-end time, then tick it once a
    // second while the modal is open. Practice has no countdown (empty string),
    // so it stays empty and no timer starts.
    const target = endOfPuzzleDay(currentPuzzleDate)
    const display: EndgameContent =
      content.countdown === ''
        ? content
        : { ...content, countdown: getCountdownLabel(target) }
    showEndgame(endgameElements, display, {
      onClose: handleEndgameClose,
      onShare: handleEndgameShare,
    })
    if (display.countdown !== '') {
      countdownTimer = setInterval((): void => {
        const label = getCountdownLabel(target)
        endgameElements.countdown.textContent = label
        // Once the rollover instant passes the label is "ready" and won't change
        // again; stop ticking. The deferred rollover applies on modal close.
        if (label === COUNTDOWN_READY_LABEL && countdownTimer !== null) {
          clearInterval(countdownTimer)
          countdownTimer = null
        }
      }, 1000)
    }
    renderStatsSection(statsEls, currentStats)
  }

  function handleEndgameClose(): void {
    // Stop the live countdown tick — the modal is closing.
    if (countdownTimer !== null) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
    // THE single place the game-over banner becomes visible. Its text was
    // rendered on the terminal transition (renderGameOverBanner), but its
    // reveal is deferred past the auto-opened results modal so the player
    // sees the finished board + win/loss animation uncovered first. Once
    // revealed it stays visible across banner-tap reopens (openEndgameModal
    // doesn't re-hide it).
    bannerEl.hidden = false
    // Apply a deferred rollover / reload now that the results modal is
    // dismissed. A rollover detected while the player was mid-game or had just
    // finished (recently active) defers; recheck() re-checks and applies it once
    // the board is no longer recently active. A pending reload (which a modal
    // hard-blocks) also fires here, and recheck() forces a fresh version probe.
    // No-op when nothing is pending (the date guard short-circuits). `refresh`
    // is constructed later in main() but this closure only runs at modal-close
    // time, long after construction.
    refresh.recheck()
  }

  function handleEndgameShare(): void {
    if (latestShareText === null) return
    // The third arg is the inline-text fallback triggerShare reveals on a hard
    // failure (insecure context: http:// over a LAN IP, where both the Web Share
    // API and the async clipboard are gated). We react to the outcome to give the
    // copy path visible feedback: browsers without the Web Share API (Firefox
    // desktop, Linux Chrome) fall to a SILENT clipboard copy, so 'copied' gets a
    // toast. 'shared' is its own feedback (the OS share sheet); 'cancelled' and
    // the inline-fallback 'failed' need none.
    void triggerShare(latestShareText, endgameShareTextEl, SHARE_URL).then(
      (outcome): void => {
        if (outcome === 'copied') {
          // Copy-path feedback (browsers without the Web Share API copy silently).
          // We flip the tapped button to "Copied!" for 2s rather than firing the
          // shared hint-toast: the toast renders BEHIND the modal's top-layer
          // backdrop and reads as greyed-out, while the in-modal button is above
          // it and sits right where the player tapped. Re-clicks restart the timer
          // so it never reverts early; reopening the modal cancels a pending
          // revert (openEndgameModal).
          endgameElements.shareButton.textContent = 'Copied!'
          if (shareCopiedRevertTimer !== null) clearTimeout(shareCopiedRevertTimer)
          shareCopiedRevertTimer = setTimeout((): void => {
            endgameElements.shareButton.textContent = 'Share'
            shareCopiedRevertTimer = null
          }, 2000)
        }
      },
    )
  }

  // Single dispatch point for both live terminal transitions and
  // restored-from-localStorage transitions. The `source` discriminator is the
  // load-bearing piece that prevents a reload from double-counting a
  // completed puzzle in lifetime stats. The `mode` discriminator routes
  // persistence: daily writes the lifetime-stats + today blob, practice writes
  // to the separate practice-progress blob and may advance the unlock cursor.
  async function handleGameEnd(
    s: GameState,
    p: Puzzle,
    source: 'live' | 'restored',
    mode: GameMode,
  ): Promise<void> {
    // Capture the mount generation this game-end belongs to. If a remount bumps
    // it during the awaited animation below, the UI tail is dropped (see guard).
    const myGeneration = mountGeneration
    if (source === 'live' && mode === 'daily') {
      // Lifetime-stats update. Pure transition; assigns a fresh object.
      const todayDayOffset = getDayOffset(new Date())
      currentStats = updateStatsOnGameEnd(
        currentStats,
        { won: s.status === 'won', undoCount: s.undoCount },
        todayDayOffset,
      )
      // Second of the two-write persist pattern (controller persisted the
      // completed record with the previous stats during dispatchAndRender;
      // we now overwrite with the updated stats). Both writes share the same
      // localStorage key so the second cleanly clobbers the first. Stamp the
      // current local date for the day-rollover detector.
      savePersistedState({
        schemaVersion: SCHEMA_VERSION,
        todayPuzzleId: p.id,
        todayLocalDate: formatLocalDate(new Date()),
        todayInProgress: null,
        todayCompleted: serializeCompleted(s),
        stats: currentStats,
        settings: currentSettings,
      })
    } else if (source === 'live' && mode === 'practice') {
      // Practice mode: never touches lifetime stats / streak / today blob.
      // Record completion in the practice-progress blob; if the completion is
      // a win on the current unlock-cursor puzzle and not the last puzzle,
      // advance the cursor by one.
      const n = currentPracticePuzzleNumber
      if (n !== null) {
        const record: PracticeCompletionRecord = {
          won: s.status === 'won',
          undoCount: s.undoCount,
          completedAtIso: new Date().toISOString(),
        }
        recordPracticeCompletion(n, record)
        // Refresh the nav so its disabled-state stays in sync (no-op today —
        // the only state the nav cares about is the current puzzle number —
        // but kept for symmetry with the mount path).
        renderPracticeNav(n)
      }
    }
    // Restored transitions: no persistence side effects.

    // Won: prefix the subtitle so it reads "SOLVED! · Letter Flick #N · Date".
    // Fires on both live and restored wins (so a refresh on a solved daily still
    // shows the prefix). Guard prevents double-prefix on repeat calls.
    if (s.status === 'won') {
      const current = puzzleSubtitleEl.textContent ?? ''
      if (current.length > 0 && !current.startsWith('SOLVED! ·')) {
        puzzleSubtitleEl.textContent = 'SOLVED! · ' + current
      }
    }

    // Live win: play the victory animation BEFORE opening the modal so the
    // player gets a moment to see their solved board. ~1.3s total; the
    // Dead Letter dances, the bar pulses in a cascading wave. Reduced-motion
    // users skip the animation (resolves immediately). Losses and restored
    // transitions open the modal without delay.
    if (source === 'live' && s.status === 'won') {
      // Hold the handle so a remount (Next/Prev) can cancel it — the cancel
      // resolves this await immediately, then the generation guard below drops
      // the now-stale celebration.
      const anim = playVictoryAnimation(boardEl, barEl)
      activeEndAnimation = anim
      await anim.done
      activeEndAnimation = null
    }

    // Live loss: board disintegrates (filled tiles + ~60% of empty cells
    // fall off the bottom; the rightmost filled bar slot pulses twice).
    // Input is locked for the duration so a stray pointer can't fire any
    // residual handlers between status transition and modal open.
    // Reduced-motion users get a 150ms fade in place of the fall.
    if (source === 'live' && s.status === 'lost') {
      controller.setInputLocked(true)
      try {
        // Cancelable like the win path: a remount (Next/Prev) cancels the fall,
        // which also tears down the global overflow lock so it can't strand.
        const anim = playLossAnimation(boardEl, barEl, p, s)
        activeEndAnimation = anim
        await anim.done
        activeEndAnimation = null
      } finally {
        controller.setInputLocked(false)
      }
    }

    // Race guard: if the board was remounted during the awaited animation, this
    // game-end belongs to a puzzle no longer on screen. The win/loss was already
    // recorded above (synchronously, pre-await); only the now-stale celebration
    // remains, and it must NOT paint over the new puzzle. Bail before any UI.
    if (myGeneration !== mountGeneration) return

    // Surface the game-over banner; the stats snapshot already reflects this
    // puzzle (on a 'restored' end the stats update happened on the original live
    // completion, persisted in the payload we just loaded).
    renderGameOverBanner(bannerEl, s, p.targetWord)
    const content = buildEndgameContent(s, p, mode)

    // Auto-open the result modal ONLY on a LIVE finish with no other dialog open.
    // - 'restored' (a reload or revisit of an already-solved puzzle): never
    //   auto-pop. A reload is a full restart, so any result modal is gone after
    //   it anyway; the player lands on the solved board + the tappable game-over
    //   banner and taps it to see / share results. No state tracked, no re-pop.
    // - live finish while another dialog (Info / Help / onboarding / About) is
    //   open: don't stack on top of it — cache + reveal the banner instead.
    // latestShareText is already set by buildEndgameContent, so a banner reopen
    // shares correctly.
    if (source === 'restored' || settingsDialog.open || onboardingDialog.open || aboutDialog.open) {
      latestEndgameContent = content
      bannerEl.hidden = false
    } else {
      openEndgameModal(content)
    }

    // Snap the board+bar back to the post-loss DOM state behind the open
    // modal, so closing the modal reveals the player's final board rather
    // than the disintegrated state the animation left.
    if (source === 'live' && s.status === 'lost') {
      restoreBoardAfterLoss(boardEl, barEl)
    }

    // LAST statement (design §13 #1): handleGameEnd is async and awaits the
    // victory/loss animation, so the endgame modal opens late (above). Running
    // the rollover re-check only now means a date-change that happened DURING
    // play is detected, sees the modal open via the safe predicate, and DEFERS
    // — handleEndgameClose's recheck() then applies it once results dismiss.
    refresh.onGameEnded()
  }

  function onGameOverBannerClick(): void {
    if (latestEndgameContent === null) return
    openEndgameModal(latestEndgameContent)
  }

  bannerEl.addEventListener('click', onGameOverBannerClick)

  // ---------- mountDaily ----------
  // Decision tree mirrors Phase 4: detect stale persistence (day rollover);
  // otherwise restore in-progress or completed state if present; otherwise
  // start fresh. Phase 6 changes: (a) day-rollover uses
  // resetTodayPreservingStats so the lifetime stats survive midnight; (b)
  // every mount path passes a `getStats` accessor so the controller's persist
  // writes always include the current stats snapshot.
  function mountDaily(): void {
    bumpMountGeneration()
    currentMode = 'daily'
    // Any non-warm-up mount is a warm-up exit by definition — release the
    // re-entrancy guard here (belt-and-suspenders over the per-exit clears)
    // so no side-door exit (e.g. the help modal's Let's Practice during a
    // REPLAY, when the openers are live) can strand it true and brick the
    // practice-1 replay button. Same for the subtitle skip affordance: strip
    // it so the daily's subtitle can never stay a live "Skip the Warm Up"
    // button (2026-06-06 review finding).
    warmUpActive = false
    clearWarmUpSkipAffordance?.()
    clearWarmUpSkipAffordance = null
    warmUpCaptionRoot.textContent = ''
    const entry = getDailyPuzzleEntry()
    const puzzle = corpusEntryToPuzzle(entry)
    const persisted = loadPersistedState()

    // Entering daily clears the practice cursor + hides the practice nav. The
    // previous mount may have been a practice puzzle (e.g. on "Back to daily"
    // after #10), so we always reset both.
    currentPracticePuzzleNumber = null
    hidePracticeNav()
    setHelpLabel('help')

    // Subtitle reads the puzzle's curator-assigned number AND date from the
    // entry data — same source on every mount path. Set BEFORE controller.mount
    // so any synchronous restored-completion onGameEnd callback sees the
    // right number.
    currentPuzzleNumber = entry.letter_flick_puzzle_number
    currentPuzzleDate = entry.letter_flick_puzzle_date
    renderPuzzleSubtitle(puzzleSubtitleEl, {
      puzzleNumber: currentPuzzleNumber,
      puzzleDate: entry.letter_flick_puzzle_date,
    })

    // Reset orchestrator-held endgame state. The previous mount (practice)
    // may have left a cached `latestEndgameContent` whose next-action
    // closures still reference the prior puzzle; clearing here keeps a
    // stale game-over-banner reopen from showing the wrong modal.
    closeEndgame(endgameElements)
    // controller.mount does not clear undoStack or remove prior event
    // listeners; mounting over a prior mount would leak both. Unmount first
    // so any in-session transition (practice -> daily, dev -> daily) starts
    // from a clean controller.
    controller.unmount()
    latestEndgameContent = null
    latestShareText = null
    bannerEl.hidden = true

    // Daily-mode hint policy: each toast fires AT MOST ONCE EVER (across
    // reloads), gated by the persisted hintFlags. Cleared via "Reset all
    // stats" in settings. Fresh closure per mountDaily call so the slot-state
    // diff starts empty.
    let flags = loadHintFlags()
    const onBarChange = createHintOnBarChange({
      canFireBlue: () => !flags.blueShown,
      markBlueFired: () => {
        flags = { ...flags, blueShown: true }
        saveHintFlags(flags)
      },
      canFireOrange: () => !flags.orangeShown,
      markOrangeFired: () => {
        flags = { ...flags, orangeShown: true }
        saveHintFlags(flags)
      },
      canFireIncorrect: () => !flags.incorrectShown,
      markIncorrectFired: () => {
        flags = { ...flags, incorrectShown: true }
        saveHintFlags(flags)
      },
    })

    const todayStr = formatLocalDate(new Date())
    // Roll over (wipe today's blob, keep lifetime stats) when the persisted
    // state does not belong to TODAY'S PUZZLE. We compare the puzzle IDENTITY,
    // not just the date: persistCurrent re-stamps todayLocalDate from the clock
    // on every flick, so a puzzle solved just after local midnight carries the
    // new day's date while still being yesterday's puzzle — a date-only check
    // would then restore yesterday's board onto today's entry (board/word/number
    // desync). Comparing puzzle ids is the robust invariant; the date check is
    // kept too because it still catches the v5 → v6 migration's '' sentinel and
    // the same-puzzle-different-day clamp at the end of the pool.
    if (
      persisted !== null &&
      (persisted.todayPuzzleId !== puzzle.id || persisted.todayLocalDate !== todayStr)
    ) {
      resetTodayPreservingStats(currentStats)
      controller.mount(boardEl, barEl, bannerEl, undoEl, puzzle, {
        persistEnabled: true,
        getStats: getCurrentStats,
        onGameEnd: (s, p, src): void => { void handleGameEnd(s, p, src, 'daily') },
        onBarChange,
        onBarSlotTap: showHintForSlot,
      })
      return
    }

    if (persisted !== null && persisted.todayCompleted !== null) {
      controller.mount(boardEl, barEl, bannerEl, undoEl, puzzle, {
        persistEnabled: true,
        restore: { kind: 'completed', record: persisted.todayCompleted },
        getStats: getCurrentStats,
        onGameEnd: (s, p, src): void => { void handleGameEnd(s, p, src, 'daily') },
        onBarChange,
        onBarSlotTap: showHintForSlot,
      })
      return
    }

    if (persisted !== null && persisted.todayInProgress !== null) {
      const undoSnapshots = persisted.todayUndoSnapshots
      controller.mount(boardEl, barEl, bannerEl, undoEl, puzzle, {
        persistEnabled: true,
        restore: {
          kind: 'inProgress',
          state: persisted.todayInProgress,
          ...(undoSnapshots !== undefined ? { undoSnapshots } : {}),
        },
        getStats: getCurrentStats,
        onGameEnd: (s, p, src): void => { void handleGameEnd(s, p, src, 'daily') },
        onBarChange,
        onBarSlotTap: showHintForSlot,
      })
      return
    }

    controller.mount(boardEl, barEl, bannerEl, undoEl, puzzle, {
      persistEnabled: true,
      getStats: getCurrentStats,
      onGameEnd: (s, p, src): void => { void handleGameEnd(s, p, src, 'daily') },
      onBarChange,
      onBarSlotTap: showHintForSlot,
    })
  }

  // ---------- mountPractice ----------
  // Mounts the 1-indexed practice puzzle `n`. No persistence to the daily
  // blob, no lifetime-stats mutation, no streak interaction — practice plays
  // are independent of the daily mechanic. Completion is recorded against the
  // separate practice-progress blob in handleGameEnd's practice branch.
  //
  // The orchestrator-held endgame caches (`latestEndgameContent`,
  // `latestShareText`) are cleared up front so a previous puzzle's
  // game-over-banner reopen can't fire with the wrong puzzle's data.
  // Practice-mode hint policy state, lifted to outer scope so the
  // createHintOnBarChange callbacks below can read/set it. These gate the
  // first-time blue / incorrect toasts to once per mount. Reset to false at
  // the top of every mountPractice so each puzzle gets a fresh hint gate.
  let practiceBlueShownThisMount = false
  let practiceIncorrectShownThisMount = false

  function mountPractice(n: number): void {
    bumpMountGeneration()
    currentMode = 'practice'
    // Warm-up exit by definition — see the matching clears in mountDaily.
    warmUpActive = false
    clearWarmUpSkipAffordance?.()
    clearWarmUpSkipAffordance = null
    practiceBlueShownThisMount = false
    practiceIncorrectShownThisMount = false
    warmUpCaptionRoot.textContent = ''
    const puzzle = getPracticePuzzle(n)
    if (puzzle === null) {
      console.error('Practice mode unavailable')
      return
    }

    // Drop any open endgame modal from a prior puzzle BEFORE unmounting the
    // controller; otherwise the modal would stay visible over the new board
    // for a frame.
    closeEndgame(endgameElements)
    controller.unmount()

    // Clear cached endgame state and the game-over banner. These were
    // populated for the prior mount's puzzle; recomputed on the next terminal
    // transition for this mount's puzzle.
    latestEndgameContent = null
    latestShareText = null
    bannerEl.hidden = true

    currentPuzzleNumber = n
    currentPracticePuzzleNumber = n
    // No puzzle subtitle in practice mode — the practice-nav row below shows
    // "Practice N / 10", which is the same information. Clearing collapses the
    // subtitle row via .puzzle-subtitle:empty { display: none } in index.css.
    puzzleSubtitleEl.textContent = ''
    renderPracticeNav(n)
    setHelpLabel('play')

    // Practice-mode hint policy: blue fires ONCE per puzzle mount (in-memory,
    // not persisted — a fresh mountPractice = a fresh blue gate). Orange
    // fires every time. Practice is a learning environment, so the orange
    // correction is always useful. Booleans live in the outer scope; this
    // function resets them at entry (above).
    const onBarChange = createHintOnBarChange({
      canFireBlue: () => !practiceBlueShownThisMount,
      markBlueFired: (): void => { practiceBlueShownThisMount = true },
      canFireOrange: () => true,
      markOrangeFired: (): void => {},
      canFireIncorrect: () => !practiceIncorrectShownThisMount,
      markIncorrectFired: (): void => { practiceIncorrectShownThisMount = true },
    })

    controller.mount(boardEl, barEl, bannerEl, undoEl, puzzle, {
      persistEnabled: false,
      onGameEnd: (s, p, src): void => { void handleGameEnd(s, p, src, 'practice') },
      onBarChange,
      onBarSlotTap: showHintForSlot,
    })
  }

  // ---------- mountWarmUp ----------
  // The "Warm Up" gate (guided onboarding curriculum). A full fourth mount
  // sibling, structurally parallel to mountPractice, replicating the established
  // teardown discipline. Consumes the controller as a black box: the warm-up
  // brain (the ordered screens + the sequencer) lives in warmup.ts; this
  // function does the ONE-TIME heavy teardown, then hands the sequencer a
  // mountScreen closure (= controller.unmount() + controller.mount(...) +
  // caption, nothing more) so per-screen remounts stay cheap and clean.
  //
  // Hint toasts are suppressed (NO createHintOnBarChange) — the captions are the
  // teaching. Undo is suppressed (suppressUndo) on every screen — failure is
  // impossible, so undo has no role. currentMode = 'practice' for the duration:
  // no new GameMode union member (avoids widening type unions across files), and
  // warm-up never reaches the shared handleGameEnd, so practice-specific endgame
  // logic can't leak. Any non-daily mode already defers rollovers/reloads (both
  // gates require mode==='daily'), so canRollOverNow / canReloadNow need zero changes.
  function mountWarmUp(): void {
    // Re-entrancy guard: a live warm-up must never be torn down by a second
    // mountWarmUp (the body rebuilds the board unconditionally). Cleared on
    // every exit below; the practice-1 replay button re-enters legitimately
    // because the flag is false by then.
    if (warmUpActive) return
    warmUpActive = true
    bumpMountGeneration()
    currentMode = 'practice'
    // ---- ONE-TIME heavy teardown (runs at warm-up ENTRY only; per-screen
    // remounts via the mountScreen seam below do NOT repeat this). ----
    warmUpCaptionRoot.textContent = ''

    // Drop any open endgame modal before unmounting; otherwise it would linger
    // over the new board for a frame.
    closeEndgame(endgameElements)
    controller.unmount()

    // Clear cached endgame state + reset the game-over banner to hidden. The
    // banner is never unhidden during warm-up (reveal lives in handleEndgameClose
    // which warm-up never reaches), but we reset it for symmetry with the other
    // mount paths.
    latestEndgameContent = null
    latestShareText = null
    bannerEl.hidden = true

    // Entering warm-up clears the practice cursor + hides the practice nav;
    // the header reads "help" (tapping it opens onboarding).
    currentPracticePuzzleNumber = null
    hidePracticeNav()
    setHelpLabel('help')

    // Subtitle reads the skip prompt — no number/date. Direct textContent (the
    // renderPuzzleSubtitle idiom always appends "#N · date", which warm-up has
    // no concept of). The subtitle doubles as the skip affordance for the
    // duration of the warm-up (wired immediately below).
    puzzleSubtitleEl.textContent = 'Welcome to Warm Ups. Tap Here to Skip'

    // ---- Warm-up SKIP affordance (subtitle-as-button) ----
    // The subtitle <p> becomes a tappable/keyboard-activatable skip control
    // that EXISTS ONLY while the warm-up is mounted. Skip = the completion
    // path minus the congrats popup, mirroring onComplete's "Let's Play"
    // branch exactly. Every warm-up exit path (skip itself + both congrats
    // forks) calls removeSkipAffordance(), which is idempotent. mountDaily /
    // mountPractice overwrite the subtitle TEXT; this cleanup removes the
    // listeners, a11y attributes, and CSS class so nothing leaks into
    // daily/practice mode.
    //
    // While a warm-up popup (welcome/congrats) is open the page is inert under
    // <dialog>.showModal(), so the subtitle is not reachable then — confirmed
    // against isWarmUpDialogOpen()/showModal usage; no extra open-popup guard
    // is needed here.
    let warmUpSkipped = false
    function skipWarmUp(): void {
      // Idempotent: a double-tap (or Enter + a queued click) must not run the
      // teardown / mountDaily twice. The first activation latches the flag and
      // immediately strips the affordance so a second event finds nothing.
      if (warmUpSkipped) return
      warmUpSkipped = true
      removeSkipAffordance()
      // Mirror onComplete's "Let's Play" branch exactly, minus the congrats
      // popup: re-stamp the gate (harmless on a replay), tear down the
      // sequencer (also clears the caption), mount today's daily, then
      // silent-re-sync the rollover guard via recheck() (see onComplete for
      // the recheck() rationale).
      setWarmUpCompletedAt(new Date().toISOString())
      // In-memory mirror: keeps the openers live this session even if the disk
      // write above silently failed (private mode). See warmUpCompletedThisSession.
      warmUpCompletedThisSession = true
      // Seed the inert first-touch flag if it was never set (write-only now —
      // nothing reads getOnboardedAt; preserved for future telemetry only).
      if (getOnboardedAt() === null) {
        setOnboardedAt(new Date().toISOString())
      }
      sequencer.teardown()
      // Warm-up exit: release the re-entrancy guard before leaving for daily.
      warmUpActive = false
      mountDaily()
      refresh.recheck()
    }
    function handleSkipKeydown(e: KeyboardEvent): void {
      // Native <button> activation semantics for a role="button": Enter and
      // Space both fire. preventDefault stops Space from scrolling the page.
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        skipWarmUp()
      }
    }
    function removeSkipAffordance(): void {
      // Idempotent — safe to call from any exit path (and twice). Removes the
      // listeners, the a11y role/tabindex/label, and the CSS class. The
      // subtitle text is left as-is; the mount path the player lands on
      // overwrites it.
      puzzleSubtitleEl.removeEventListener('click', skipWarmUp)
      puzzleSubtitleEl.removeEventListener('keydown', handleSkipKeydown)
      puzzleSubtitleEl.classList.remove('puzzle-subtitle--warmup-skip')
      puzzleSubtitleEl.removeAttribute('role')
      puzzleSubtitleEl.removeAttribute('tabindex')
      puzzleSubtitleEl.removeAttribute('aria-label')
    }
    puzzleSubtitleEl.classList.add('puzzle-subtitle--warmup-skip')
    puzzleSubtitleEl.setAttribute('role', 'button')
    puzzleSubtitleEl.setAttribute('tabindex', '0')
    puzzleSubtitleEl.setAttribute('aria-label', 'Skip the Warm Up')
    puzzleSubtitleEl.addEventListener('click', skipWarmUp)
    puzzleSubtitleEl.addEventListener('keydown', handleSkipKeydown)
    // Register the module-scope cleanup handle so SIDE-DOOR exits (any
    // non-warm-up mount) can strip this affordance too — without it, a replay
    // abandoned via the help modal's Let's Practice would leave the daily's
    // subtitle a live "Skip the Warm Up" button (2026-06-06 review finding).
    clearWarmUpSkipAffordance = removeSkipAffordance

    // The mountScreen seam: EXACTLY controller.unmount() + controller.mount(...)
    // + (the sequencer renders the caption itself). persistEnabled:false is
    // baked in here; undo is suppressed on every screen EXCEPT the undo-lesson
    // screen (hooks.allowUndo). The sequencer forwards the screen's moveFilter
    // + onBarChange, onUndo ONLY for the undo lesson, and onGameEnd ONLY for
    // the finale.
    function mountScreen(puzzle: Puzzle, hooks: MountScreenHooks): void {
      controller.unmount()
      controller.mount(boardEl, barEl, bannerEl, undoEl, puzzle, {
        persistEnabled: false,
        suppressUndo: hooks.allowUndo !== true,
        moveFilter: hooks.moveFilter,
        onBarChange: (): void => { hooks.onBarChange() },
        // Undo-lesson screen only: the controller's onUndo advances the
        // sequencer's caption after the player's first real Undo.
        ...(hooks.onUndo !== undefined ? { onUndo: hooks.onUndo } : {}),
        // Finale only: the controller's onGameEnd fires the sequencer's
        // completion driver. Every other screen omits it — a 1-slot 'won' must
        // NOT stamp the flag / mount the daily.
        ...(hooks.onGameEnd !== undefined
          ? { onGameEnd: hooks.onGameEnd }
          : {}),
      })
    }

    // Completion: fires inside the controller's live render stack on the
    // finale's winning flick (bar spells ZEN), so it does almost nothing
    // synchronously — stamp the flag, then defer by a MICROTASK. NEVER call
    // mountDaily() synchronously inside onGameEnd (the controller uses the
    // same deferral for restored completions). The deferred body: teardown the
    // sequencer (clears the caption) and show the congrats popup over the
    // solved ZEN board (warm-up-owned, replaces the former "Well done!"
    // toast). Its Let's Play button (or Escape — never soft-lock) mounts the
    // daily and re-syncs the rollover guard.
    function onComplete(): void {
      setWarmUpCompletedAt(new Date().toISOString())
      // In-memory mirror: keeps the openers live this session even if the disk
      // write above silently failed (private mode). See warmUpCompletedThisSession.
      warmUpCompletedThisSession = true
      // Seed the inert first-touch flag if it was never set (write-only now —
      // nothing reads getOnboardedAt; preserved for future telemetry only).
      if (getOnboardedAt() === null) {
        setOnboardedAt(new Date().toISOString())
      }
      queueMicrotask((): void => {
        // Normal completion is a warm-up EXIT — strip the subtitle skip
        // affordance (listeners + a11y attrs + CSS class) before the congrats
        // popup remounts daily/practice. Idempotent: harmless if the player
        // had also tapped Skip in the same frame (skipWarmUp latches first).
        removeSkipAffordance()
        sequencer.teardown()
        showWarmUpComplete(
          // Let's Play (also the Escape fallback) → today's daily.
          (): void => {
            // Warm-up exit: release the re-entrancy guard before leaving.
            warmUpActive = false
            mountDaily()
            // Silent guard re-sync: the coordinator exposes no dedicated "sync
            // date without announcing" hook, so we use recheck(). mountDaily
            // has just landed on the correct (possibly new) day's puzzle; on a
            // crossed-midnight day recheck() at worst fires the a11y rollover
            // toast once — acceptable, and strictly better than a stale guard
            // firing a redundant remount on the player's next focus event.
            refresh.recheck()
          },
          // Let's Practice → practice puzzle 1 (the gate is already stamped;
          // the help-modal Let's Play later routes to the daily as normal).
          (): void => {
            // Warm-up exit: release the re-entrancy guard before leaving.
            warmUpActive = false
            mountPractice(1)
          },
        )
      })
    }

    // Build the single session sequencer — it owns the caption host + the
    // ordered screens for the whole run. The welcome splash (warm-up-owned
    // popup, visual copy of the onboarding modal) shows first; the sequencer
    // starts — mounting the first screen (push) — only when the player
    // continues (any close path continues; the gate must never soft-lock).
    const sequencer = createWarmUpSequencer({
      captionHost: warmUpCaptionRoot,
      mountScreen,
      onComplete,
    })
    showWarmUpWelcome((): void => {
      sequencer.start()
    })
  }

  // Render the practice navigation bar for puzzle `n`. Prev/next are always
  // enabled — they wrap at the corpus boundaries (puzzle 1's prev → 10,
  // puzzle 10's next → 1). All 10 puzzles are freely accessible.
  //
  // The Warm Up button is visible only on puzzle #1 (the puzzle the Warm Up
  // replay is scripted for). Visibility is recomputed every render so a
  // prev/next navigation away from / back to #1 toggles it cleanly.
  function renderPracticeNav(currentN: number): void {
    practiceNavRoot.hidden = false
    const showWarmUpBtn = currentN === 1
    if (showWarmUpBtn) {
      practicePositionRoot.textContent = `Practice\n${String(currentN)}/${String(PRACTICE_PUZZLE_COUNT)}`
      practicePositionRoot.classList.add('practice-nav__position--stacked')
    } else {
      practicePositionRoot.textContent = `Practice ${String(currentN)} / ${String(PRACTICE_PUZZLE_COUNT)}`
      practicePositionRoot.classList.remove('practice-nav__position--stacked')
    }
    warmUpReplayBtn.hidden = !showWarmUpBtn
  }

  function hidePracticeNav(): void {
    practiceNavRoot.hidden = true
    practicePositionRoot.textContent = ''
    warmUpReplayBtn.hidden = true
  }

  // Warm Up button (shown on practice puzzle #1): launches the Warm Up
  // curriculum as a replay. mountWarmUp's entry teardown hides the practice
  // nav, so the button can't re-fire mid-warm-up; the congrats fork's "Let's
  // Practice" lands back on practice 1.
  warmUpReplayBtn.addEventListener('click', (): void => {
    mountWarmUp()
  })

  // The header's left button label is mode-aware: "help" while playing
  // the daily (tapping it opens the onboarding), and "play" while in
  // practice mode (tapping it opens the onboarding whose primary action
  // sends the player to the daily). Behavior of the button is identical
  // in both modes; only the label changes.
  function setHelpLabel(text: 'help' | 'play'): void {
    if (helpLabelEl === null) return
    helpLabelEl.textContent = text
  }

  // Practice prev/next click handlers — wired ONCE here (not inside
  // renderPracticeNav, which would stack a new listener on every mount).
  // Each handler reads the live `currentPracticePuzzleNumber` so the same
  // listener correctly targets the currently-mounted puzzle no matter how
  // many times the player has navigated. Both wrap at the corpus boundaries.
  practicePrevBtn.addEventListener('click', (): void => {
    // No mid-celebration block: navigating cancels any in-flight victory
    // animation (via bumpMountGeneration in mountPractice) and loads the puzzle
    // immediately, so fast drilling through practice stays snappy.
    const n = currentPracticePuzzleNumber
    if (n === null) return
    // 1 → 10, otherwise n - 1.
    mountPractice(n <= 1 ? PRACTICE_PUZZLE_COUNT : n - 1)
  })
  practiceNextBtn.addEventListener('click', (): void => {
    const n = currentPracticePuzzleNumber
    if (n === null) return
    // On the LAST practice puzzle, NEXT no longer silently wraps to #1.
    // Instead it intercepts and shows the practice-completion popup (warm-up
    // dialog chrome) — fires EVERY time, for all players. Its large blue
    // "Let's Play" (and the Escape fallback) leaves practice for the daily;
    // the small "practice again" secondary does what the wrap would have done
    // (mount practice #1). Otherwise NEXT advances n → n + 1 as before.
    if (n >= PRACTICE_PUZZLE_COUNT) {
      showPracticeComplete(
        // Let's Play (also the Escape fallback) → leave practice for the daily.
        // Mirror the help-modal "Let's Play" practice-exit exactly: the daily
        // inherits the Warm Up gate, so an absent flag routes to the Warm Up
        // instead (defensive — a NEXT-on-#10 presser has normally already
        // finished the warm up, but this popup fires for all players).
        (): void => {
          if (getWarmUpCompletedAt() === null) {
            mountWarmUp()
          } else {
            mountDaily()
          }
        },
        // practice again → practice puzzle 1 (exactly the former NEXT wrap).
        (): void => {
          mountPractice(1)
        },
      )
      return
    }
    mountPractice(n + 1)
  })

  // Closure over `currentStats` so the controller always sees the latest
  // snapshot at persist time. Declared as a named function (not an arrow at
  // each call site) so the same identity is passed to every mount path — and
  // so the type signature matches MountOptions.getStats.
  function getCurrentStats(): LifetimeStats {
    return currentStats
  }

  // ---------- Auto-refresh (left-open tab) ----------
  // Tier 1 (day rollover in place) + Tier 2 (new-release pickup). See
  // docs/design-specs/2026-05-31-web-auto-refresh.md. The coordinator owns the
  // triggers + timers; main() supplies the safe predicate + the actions, which
  // read live closure state the coordinator can't reach.

  // True when ANY full-screen dialog is open. A live disruption (re-mount or
  // reload) while one of these is open would yank it out from under the player
  // — and mountDaily() itself calls closeEndgame() + controller.unmount(), so
  // rolling over behind an open endgame modal would close it mid-read. This is
  // load-bearing, not cosmetic (design §13 #2).
  function isAnyDisruptiveModalOpen(): boolean {
    return (
      endgameDialog.open ||
      settingsDialog.open ||
      onboardingDialog.open ||
      aboutDialog.open ||
      // Warm-up popups are dynamically-created <dialog>s (not pre-rendered in
      // index.html), so they aren't covered by the static refs above. A live
      // re-mount/reload behind an open warm-up splash would yank it out from
      // under the player just like the others (A3).
      isWarmUpDialogOpen()
    )
  }

  // Live snapshot feeding the two gate predicates. Thin adapter over the pure
  // canRollOver / canReload — reads live closure + controller state and forwards
  // raw values, keeping the crux logic unit-testable (design §10.8). The gate is
  // keyed on INVESTMENT in this board (hasCommittedMove + how long since the last
  // flick/undo), NOT on status==='playing' or "a modal is open".
  function disruptInputs(): DisruptInputs {
    const last = controller.lastActivityAt
    return {
      mode: currentMode,
      status: controller.status,
      isAnimating: controller.isAnimating,
      isGestureActive: controller.isGestureActive,
      hasCommittedMove: controller.hasCommittedMove,
      msSinceLastActivity: last === null ? null : Date.now() - last,
      isModalOpen: isAnyDisruptiveModalOpen(),
    }
  }
  // Rollover gate (aggressive — modals never block it). Reload gate (gentle —
  // defers while a modal is open).
  function canRollOverNow(): boolean {
    return canRollOver(disruptInputs())
  }
  function canReloadNow(): boolean {
    return canReload(disruptInputs())
  }

  // Tier-1 polite a11y announcement after a no-reload rollover. Surfaced as a
  // self-dismissing hint toast (#hint-toast-root carries aria-live=polite, so
  // the announcement is read out). The toast drops in, holds, and auto-clears
  // on its own — no ordering contract with mountDaily's render path.
  function announceRollover(localDate: string): void {
    const dateLabel = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(`${localDate}T00:00:00`))
    showHintToast(`New puzzle loaded for ${dateLabel}.`)
  }

  const refresh = createRefreshCoordinator(
    {
      mountDaily,
      canRollOverNow,
      canReloadNow,
      announceRollover,
      autoReload: (): void => {
        window.location.reload()
      },
    },
    LOADED_BUILD_ID,
  )
  refresh.start()

  // Install the iOS background→resume modal-lockup healer UNCONDITIONALLY here,
  // NOT gated behind the onboarding fork below. A resume that happens WHILE the
  // onboarding (or warm-up) modal is open is the single most likely lockup
  // scenario — a player opens the page, gets the onboarding modal, switches
  // apps, comes back. So the reconciler must be live before any modal can show.
  // onResume resets the double-tap latch so the first tap after a long
  // background gap always registers.
  installResumeReconciler({ onResume: resetDoubleTapState })

  // ---------- Settings overlay ----------
  // Settings markup lives in index.html (Phase 7 added the gear + dialog).
  // Wire the open/close + the dark-mode radios + the reset-stats button.
  // Pre-check the radio that matches the persisted dark-mode setting so the
  // form reflects current state on first open.
  for (const radio of settingsDialogEl.querySelectorAll<HTMLInputElement>('input[name="darkMode"]')) {
    radio.checked = radio.value === currentSettings.darkMode
    radio.addEventListener('change', () => {
      if (!radio.checked) return
      const value = radio.value
      if (value !== 'auto' && value !== 'light' && value !== 'dark') return
      currentSettings = { darkMode: value }
      applySettings(currentSettings)
      // Persist immediately so the choice survives reload. Build the same
      // shape persistCurrent would have written; gracefully no-op for fresh
      // sessions where nothing is on disk yet.
      const persisted = loadPersistedState()
      if (persisted !== null) {
        savePersistedState({ ...persisted, settings: currentSettings })
      }
    })
  }
  settingsCloseBtnEl.addEventListener('click', () => {
    settingsDialogEl.close()
  })

  onboardingCloseBtnEl.addEventListener('click', () => {
    closeOnboarding(onboardingDialogEl)
  })
  settingsResetBtnEl.addEventListener('click', () => {
    const ok = window.confirm(
      'Reset all stats, streaks, today’s puzzle, and practice progress? This cannot be undone.',
    )
    if (!ok) return
    // wipeAllPersistence removes the game-state blob, practice-progress blob,
    // and onboarding flag. Reload so the orchestrator boots from a clean state
    // (next visit shows the Warm Up, daily starts fresh, practice cursor at 1).
    wipeAllPersistence()
    // Warm-up's flag lives under its own key (owned by warmup.ts, not the
    // versioned persistence schema), so clear it here too → the full new-player
    // experience, including the Warm Up gate, replays.
    clearWarmUpCompletedAt()
    // Close the modal BEFORE the reload: the old document stays on screen
    // (inert under the dialog) until the new response commits, so a slow or
    // unreachable origin would otherwise leave the user staring at a spent
    // modal that reads as a full lockup. Closing first leaves the live game
    // visible while the reload is pending.
    settingsDialogEl.close()
    window.location.reload()
  })
  settingsPracticeBtnEl.addEventListener('click', () => {
    // Always dismiss the Information popup on tap. The gear stays reachable
    // during the warm-up (Reset All Stats must work), but the Practice escape
    // inside it does not advance until the warm-up is done — closing the popup
    // is the intended behavior in that state (avoids a stuck Information modal).
    settingsDialogEl.close()
    if (!isWarmUpDone()) return
    mountPractice(1)
  })
  settingsButton.addEventListener('click', () => {
    // Re-sync the radios before opening in case currentSettings changed via
    // any other path (none today, but cheap insurance).
    for (const radio of settingsDialogEl.querySelectorAll<HTMLInputElement>('input[name="darkMode"]')) {
      radio.checked = radio.value === currentSettings.darkMode
    }
    settingsDialogEl.showModal()
  })

  aboutButton.addEventListener('click', () => {
    aboutDialogEl.showModal()
  })
  aboutCloseBtnEl.addEventListener('click', () => {
    aboutDialogEl.close()
  })
  aboutDoneBtnEl.addEventListener('click', () => {
    aboutDialogEl.close()
  })

  // Show / hide the "Let's Practice" button based on whether the practice
  // corpus loaded successfully. Called immediately before each
  // `showOnboarding(...)` invocation so we react to any pathological case
  // (e.g. bundled JSON failed validation post-deploy) without forcing the
  // player into an unselectable CTA.
  function syncPracticeCtaVisibility(): void {
    const practiceBtn = onboardingDialog.querySelector('#onboarding-practice')
    if (practiceBtn instanceof HTMLButtonElement) {
      practiceBtn.hidden = !isPracticeAvailable()
    }
  }

  // Help re-opens the onboarding modal. The first-time `showOnboarding`
  // listener self-removes after the first close (ui/modal.ts), so re-opening
  // here doesn't risk re-triggering mountDaily(). The form's `Let's Play` and
  // `Let's Practice` buttons close natively on submit; we just route to the
  // right behavior based on the current mount. Each branch only remounts when
  // the player is actually switching modes — otherwise we'd discard in-progress
  // state (e.g. half-solved daily, or a freshly-shuffled practice puzzle).
  // currentPracticePuzzleNumber !== null is our "currently on practice" check.
  helpButton.addEventListener('click', () => {
    // Warm Up gate — ONE check covers all three openers. The brand title
    // delegates here via helpButton.click(), and practice mode's "play" header
    // button is this same button relabeled (setHelpLabel). Pre-stamp, every
    // opener is inert so the welcome modal (and its Let's Practice escape)
    // can't surface during the warm-up. Post-stamp they work instantly —
    // including via the in-memory session mirror in private mode.
    if (!isWarmUpDone()) return
    syncPracticeCtaVisibility()
    showOnboarding(onboardingDialogEl, {
      onPlay: (): void => {
        closeOnboarding(onboardingDialogEl)
        if (currentPracticePuzzleNumber !== null) {
          // Practice → daily is the one in-app exit into the daily; it inherits
          // the Warm Up gate. Flag absent → Warm Up instead of the daily.
          if (getWarmUpCompletedAt() === null) {
            mountWarmUp()
          } else {
            mountDaily()
          }
        }
      },
      onPractice: (): void => {
        closeOnboarding(onboardingDialogEl)
        // If the player is already on practice, don't tear down their
        // current puzzle. Otherwise mount puzzle #1. The demo is opt-in via
        // the "Play Demo" button on the practice nav.
        if (currentPracticePuzzleNumber !== null) return
        mountPractice(1)
      },
      // X-button (or Escape) closes the rules-reference view without
      // changing modes. Without this, the empty returnValue would fall
      // through to onPlay and a practice player would land on daily —
      // the reported bug.
      onDismiss: (): void => {},
    })
  })

  // Tapping the centered "Letter Flick" brand opens the welcome modal — the
  // same flow the "?" help button drives. Routes confused players who can't
  // find the small help glyph back to the play/practice fork without any
  // visual affordance on the title itself.
  brandTitleEl.addEventListener('click', () => helpButton.click())

  // ---------- URL-driven mode routing ----------
  // External pages (notably /how-to-play.html) can deep-link to practice via
  // ?mode=practice. The query string is cleared via history.replaceState in
  // both branches so a refresh after this load returns to the normal flow
  // rather than re-triggering on every load.
  //
  // The Warm Up gate applies here too: a visitor with no completed warm-up
  // gets the warm-up, full stop — the practice intent is NOT preserved through
  // it (after the congrats popup the player picks Practice or Play). Only a
  // visitor who has already finished the warm-up routes straight to practice.
  const urlMode = new URLSearchParams(window.location.search).get('mode')
  if (urlMode === 'practice') {
    window.history.replaceState(null, '', window.location.pathname)
    if (!isWarmUpDone()) {
      mountWarmUp()
      return
    }
    mountPractice(1)
    return
  }

  // ---------- Warm Up gate (boot) ----------
  // The one rule: until the Warm Up is completed (or skipped) once, the app
  // does NOTHING but the Warm Up. The welcome popup is gone from the boot path
  // entirely — the warm-up welcome popup is now the app's first touch. The old
  // onboarding modal survives only as the post-completion rules reference,
  // reachable via the help/brand/play opener (gated above). After the stamp,
  // every boot lands on the daily exactly as before.
  if (getWarmUpCompletedAt() === null) {
    mountWarmUp()
  } else {
    mountDaily()
  }
}

main()
